import React, { memo, useMemo } from "react";
import {
  HStack,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";

import { DynamicRulesIcon } from "modules/icons";
import UserName from "modules/_shared/user-name";
import { InternalAdminDistributionGroupFragment } from "gql/graphql";
import { getUserFullName, sortUsersByFullName } from "modules/utils/Functions";
import { CALLOUT_KEY } from "modules/utils/Constants";
import useInternalAdminDistributionGroups from "../hooks/use-internal-admin-distribution-groups";
import useInternalAdminUsers from "../hooks/use-internal-admin-users";
import useInternalAdminUserList from "../hooks/use-internal-admin-user-list";
import useInternalAdminDistributionGroupQuery from "../hooks/use-internal-admin-distribution-group-query";

interface IProps {
  distributionGroup: InternalAdminDistributionGroupFragment;
}

const InternalAdminDistributionGroupMembershipCriteria = ({
  distributionGroup,
}: IProps) => {
  const { distributionGroups } = useInternalAdminDistributionGroups(
    distributionGroup.distributionGroupIdList,
  );

  const { users: addedUsers } = useInternalAdminUsers(
    distributionGroup.addedUserIdList,
  );

  const { naturalLanguageQuery } = useInternalAdminDistributionGroupQuery({
    query: distributionGroup.queryBuilder
      ? JSON.parse(distributionGroup.queryBuilder)
      : undefined,
  });
  const addedUserCount = distributionGroup?.addedUserIdList.length;

  const addedDistributionGroupCount =
    distributionGroup?.distributionGroupIdList.length ?? 0;

  const addedUserNames = useMemo(
    () =>
      [...addedUsers]
        .sort(sortUsersByFullName)
        .map((user) => getUserFullName(user))
        .join(", "),
    [addedUsers],
  );

  const groupNames = useMemo(
    () =>
      distributionGroups
        .map((distributionGroup) => `${CALLOUT_KEY}${distributionGroup.name}`)
        .join(", "),
    [distributionGroups],
  );

  const { users: usersInAllGroups } = useInternalAdminUserList({
    organizationId: distributionGroup.organizationId,
    input: {
      allDistributionGroups: true,
      offset: 0,
      limit: 1000,
    },
  });

  return (
    <VStack
      w="full"
      alignItems="flex-start"
      bg={colors.whiteTertiary}
      p={2}
      borderRadius={4}
    >
      <HStack w="full">
        <DynamicRulesIcon boxSize={4} fill={colors.blackSecondary} />
        <Text fontSize={16} fontWeight={700} color={colors.blackSecondary}>
          Membership Criteria
        </Text>
      </HStack>
      {distributionGroup.isAllUsers ? (
        <HStack spacing={1}>
          <Text fontSize={14} fontWeight={700} color={colors.gray50}>
            Global:
          </Text>
          <Text fontSize={14} fontWeight={500} color={colors.gray50}>
            All Active Users
          </Text>
        </HStack>
      ) : (
        <>
          {(addedUserCount > 0 || addedDistributionGroupCount > 0) && (
            <VStack w="full" alignItems="flex-start" spacing={0}>
              <Text fontSize={14} fontWeight={700} color={colors.gray50}>
                Users & Groups
              </Text>
              <UnorderedList
                pl={4}
                color={colors.gray50}
                fontSize={14}
                fontWeight={500}
              >
                {addedUserCount > 0 && (
                  <ListItem>
                    <Text>Users:{` ${addedUserNames}`}</Text>
                  </ListItem>
                )}
                {addedDistributionGroupCount > 0 && (
                  <ListItem>
                    <Text>Groups:{` ${groupNames}`}</Text>
                  </ListItem>
                )}
              </UnorderedList>
            </VStack>
          )}
          {naturalLanguageQuery && (
            <VStack w="full" alignItems="flex-start" spacing={0}>
              <Text fontSize={14} fontWeight={700} color={colors.gray50}>
                Dynamic Rules
              </Text>
              <UnorderedList
                pl={4}
                color={colors.gray50}
                fontSize={14}
                fontWeight={500}
              >
                <ListItem>{naturalLanguageQuery}</ListItem>
              </UnorderedList>
            </VStack>
          )}
          {usersInAllGroups.length > 0 && (
            <VStack w="full" alignItems="flex-start" spacing={0}>
              <Text fontSize={14} fontWeight={700} color={colors.gray50}>
                Included in All Groups ({usersInAllGroups.length})
              </Text>
              <UnorderedList
                pl={4}
                color={colors.gray50}
                fontSize={14}
                fontWeight={500}
              >
                <ListItem>
                  <Text>
                    {usersInAllGroups.map((user) => (
                      <UserName key={user.id} userId={user.id} />
                    ))}
                  </Text>
                </ListItem>
              </UnorderedList>
            </VStack>
          )}
        </>
      )}
    </VStack>
  );
};

export default memo(InternalAdminDistributionGroupMembershipCriteria);
